import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import {
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
  GateConfigDTO,
  PeGateConfig,
  PeInterfaceConfig,
  PlantConfigStep,
  RiserInterfaceMode
} from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { noAppartmentOptions, RiserInterfaceModeCombo } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import GateConfigComponent from '../GateConfigComponent/GateConfigComponent.vue';



@Options({
  name: 'DialogEntrancePlantCreateSetupRiser',
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    structureCreate: {
      required: true,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },   
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    "InterfaceComboMode": function(value: RiserInterfaceModeCombo) {
      this.updateInterfacesToConfigure(value);
    },
    "mainScsAddress": function() {
      this.updateInterfacesAddress();
    }
  },
  components: { NumberButtons, GateConfigComponent },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateSetupRiser extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;

  step = 0;
  currentRiserIndex = 0;

  readonly MIN_SCS_ADDRESS_INTERFACE = 1;
  readonly MAX_SCS_ADDRESS_INTERFACE = 39;
  mainScsAddress = 0;
  riserInterfacesToConfigure: { [key: string]: PeInterfaceConfig[] } = {};
  availableScsAddressInterface: number[] = [];
  
  RiserInterfaceModeCombo = RiserInterfaceModeCombo;
  InterfaceComboMode: RiserInterfaceModeCombo = RiserInterfaceModeCombo.Mode5IndipendentRiser;

  addrNotValid: boolean[] = [];
  invalidAddrReason: string[] = [];

  gatesToConfigure: { [key: string]: GateConfigDTO } = {};
  anyScsGateError = false;

  get pageTitle(): string {
    return this.$t('AppResourceEP.SetupRiserTitleWithIndex', [this.currentRiserIndex]) as string;
  }

  get currentRiserId(): string {
    if (this.structureCreate.pePlantConfig?.pePlantConfig?.risers == undefined) {
      return '';
    }
    return this.structureCreate.pePlantConfig?.pePlantConfig?.risers[this.currentRiserIndex]?.id ?? '';
  }

  get currentRiserInterfaces() {
    const currentRiserId = this.currentRiserId;
    return this.riserInterfacesToConfigure[currentRiserId] || [];
  }
  
  get currentRiser() {
    if (this.structureCreate.pePlantConfig?.pePlantConfig?.risers == undefined) {
      return null;
    }
    return this.structureCreate.pePlantConfig?.pePlantConfig?.risers[this.currentRiserIndex] || null;
  }

  get nextEnabled(): boolean {    
    if (this.step === 0) {
      return this.addrNotValid.every(error => error === false) && this.currentRiser?.name !== undefined && this.currentRiser?.name.trim() !== '';
    }
    if (Object.keys(this.gatesToConfigure).length === 0) return true;
    return Object.values(this.gatesToConfigure).every((gate) => {
      const devicesSelected = (gate.newPeSelected === true || gate.keypadSelected === true || gate.smartReaderSelected === true);
      return devicesSelected && !this.anyScsGateError;
    });
  }
  
  get availableScsAddressGate(): number[] {
    return this.plant.scsAddressHelper?.availableScsAddressGate ?? [];
  }

  get currentRiserGatesCount(): number {
    const firstInterface = this.currentRiserInterfaces[0];
    return firstInterface?.gatesCount ?? 0;
  }

  get hasMoreRisers(): boolean {
    const totalRisers = this.structureCreate.pePlantConfig?.numberOfRisers ?? 0;
    return this.currentRiserIndex < totalRisers - 1;
  }

  get currentRiserGates(): PeGateConfig[] {
    const allGates = this.structureCreate.pePlantConfig?.pePlantConfig?.gates ?? [];
    const mainInterfaceId = this.currentRiserInterfaces[0]?.id;
    return allGates.filter(gate => gate.peInterfaceConfigId && gate.peInterfaceConfigId === mainInterfaceId);
  }

  get anyScsError(): boolean {
    return this.addrNotValid.some(error => error === true);
  }

  noAppartmentOptions() {
    return noAppartmentOptions(100);
  } 

  mounted(): void {
    this.initializeData();
  }

  initializeData(): void {
    this.cleanupOrphanedRisersConfig();
    this.availableScsAddressInterface = Array.from(
      { length: this.MAX_SCS_ADDRESS_INTERFACE },
      (_, i) => i + 1
    );
    this.riserInterfacesToConfigure = this.structureCreate.pePlantConfig?.riserInterfacesConfig || {};

    this.initializeDataForCurrentRiser()
  }

  defaultRiserInterface(riserId: string): PeInterfaceConfig {
    return new PeInterfaceConfig({
      peRiserConfigId: riserId,
      mode: RiserInterfaceMode.Mode5IndipendentRiser,
      switchboardsCount: 0,
      numberOfApartments: 1,
      gatesCount: 1,
    });
  }

  getValidScsAddressForNewInterface(): number {
    const usedAddresses = Object.values(this.riserInterfacesToConfigure)
      .flat()
      .map((intf: PeInterfaceConfig) => intf.scsAddress!)
      .filter((addr: number | undefined) => addr !== undefined && addr !== null)
      .map((addr: number) => Number(addr));
    
    for (let addr = this.MIN_SCS_ADDRESS_INTERFACE; addr <= this.MAX_SCS_ADDRESS_INTERFACE; addr++) {
      const availableInPlant = this.availableScsAddressInterface.includes(addr) ?? false;
      const usedByOtherInterface = usedAddresses.includes(addr);
      
      if (availableInPlant && !usedByOtherInterface) {
        return addr;
      }
    }
    
    return this.MAX_SCS_ADDRESS_INTERFACE;
  }

  getInterfaceDescription(mode: RiserInterfaceMode | undefined, index: number, totInterfaces: number) {
    if (mode === RiserInterfaceMode.Mode5IndipendentRiser || 
        mode === RiserInterfaceMode.Mode6ExtendedRiser || 
        (mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces === 2)) {
      return `${this.$t("AppResourceEP.RiserInterface")} ${mode}`;
    }

    if (mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces > 2) {
      const orderValue = index === 1 ? this.$t("AppResourceEP.First") : this.$t("AppResourceEP.Second");
      return `${this.$t("AppResourceEP.RiserInterface")} ${mode} (${orderValue})`;
    }
  }

  updateInterfacesAddress(): void {
    const currentRiserId = this.currentRiserId;
    const interfaces = this.riserInterfacesToConfigure[currentRiserId] || [];

    interfaces.forEach((intf: PeInterfaceConfig, index: number) => {
      intf.scsAddress = Number(this.mainScsAddress) + index;
    });

    this.validateAddresses();
  }

  validateAddresses() {
    const currentRiserId = this.currentRiserId;
    const interfaces = this.riserInterfacesToConfigure[currentRiserId] || [];

    // Get addresses used by current riser's interfaces
    const currentRiserAddresses = Object.values(interfaces)
      .map((intf: PeInterfaceConfig) => intf.scsAddress!)
      .filter((addr: number | undefined) => addr !== undefined && addr !== null)
      .map((addr: number) => Number(addr));

    // Get IDs of previous risers (risers with index < currentRiserIndex)
    const previousRiserIds = (this.structureCreate.pePlantConfig?.pePlantConfig?.risers ?? [])
      .slice(0, this.currentRiserIndex)
      .map(riser => riser.id)
      .filter((id): id is string => id !== undefined);

    // Get addresses used by PREVIOUS risers' interfaces only
    const previousRisersAddresses = Object.entries(this.riserInterfacesToConfigure)
      .filter(([riserId]) => previousRiserIds.includes(riserId))
      .flatMap(([, intfs]) => intfs)
      .map((intf: PeInterfaceConfig) => intf.scsAddress!)
      .filter((addr: number | undefined) => addr !== undefined && addr !== null)
      .map((addr: number) => Number(addr));

    interfaces.forEach((intf: PeInterfaceConfig, index: number) => {
      if (intf.scsAddress === undefined || intf.scsAddress === null) {
        this.addrNotValid[index] = true;
        this.invalidAddrReason[index] = '';
        return;
      }
      
      const value = Number(intf.scsAddress);
      const acceptedRange = value >= this.MIN_SCS_ADDRESS_INTERFACE && value <= this.MAX_SCS_ADDRESS_INTERFACE;
      
      // Verifica se è disponibile nella lista del plant
      const availableInPlant = this.availableScsAddressInterface.includes(value) ?? false;
      
      // Verifica se è usato da altre interface dello stesso riser (escludendo se stesso)
      const usedByOtherInterfaceSameRiser = currentRiserAddresses.filter((addr: number) => addr === value).length > 1;
      
      // Verifica se è usato da interface di riser precedenti
      const usedByPreviousRiser = previousRisersAddresses.includes(value);
      
      this.addrNotValid[index] = !acceptedRange || !availableInPlant || usedByOtherInterfaceSameRiser || usedByPreviousRiser;
      
      if (!acceptedRange) {
        this.invalidAddrReason[index] = this.$t('entrancePlant.riserInterfaceScsAddressOutOfRange');
      } else if (!availableInPlant) {
        this.invalidAddrReason[index] = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
      } else if (usedByOtherInterfaceSameRiser || usedByPreviousRiser) {
        this.invalidAddrReason[index] = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
      } else {
        this.invalidAddrReason[index] = '';
      }
    });
  }

  updateInterfacesToConfigure(value: RiserInterfaceModeCombo): void {
    const currentRiserId = this.currentRiserId;
    const existingInterfaces = this.riserInterfacesToConfigure[currentRiserId] || [];
    const baseInterface = existingInterfaces[0];
    const newInterfaces: PeInterfaceConfig[] = [];

    switch (value) {
      case RiserInterfaceModeCombo.Mode5IndipendentRiser:
        baseInterface.mode = RiserInterfaceMode.Mode5IndipendentRiser;
        newInterfaces.push(baseInterface);
        break;

      case RiserInterfaceModeCombo.Mode6ExtendedRiserAndOneMode7ExtendedRiserExpansion:
        baseInterface.mode = RiserInterfaceMode.Mode6ExtendedRiser;
        newInterfaces.push(baseInterface);
        newInterfaces.push(this.getOrCreateExpansionInterface(existingInterfaces, currentRiserId, 1));
        break;

      case RiserInterfaceModeCombo.Mode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion:
        baseInterface.mode = RiserInterfaceMode.Mode6ExtendedRiser;
        newInterfaces.push(baseInterface);
        newInterfaces.push(this.getOrCreateExpansionInterface(existingInterfaces, currentRiserId, 1));
        newInterfaces.push(this.getOrCreateExpansionInterface(existingInterfaces, currentRiserId, 2));
        break;
    }
    this.addrNotValid = this.addrNotValid.slice(0, newInterfaces.length);
    this.invalidAddrReason = this.invalidAddrReason.slice(0, newInterfaces.length);
    this.riserInterfacesToConfigure[currentRiserId] = newInterfaces;
    this.validateAddresses();
  }

  private getOrCreateExpansionInterface(
    existingInterfaces: PeInterfaceConfig[],
    riserId: string,
    index: number
  ): PeInterfaceConfig {
    if (existingInterfaces.length > index) {
      const existingInterface = existingInterfaces[index];
      existingInterface.mode = RiserInterfaceMode.Mode7ExtendedRiserExpansion;
      existingInterface.gatesCount = 0;
      existingInterface.switchboardsCount = 0;
      return existingInterface;
    }

    const newInterface = this.defaultRiserInterface(riserId);
    newInterface.mode = RiserInterfaceMode.Mode7ExtendedRiserExpansion;
    newInterface.scsAddress = Number(this.mainScsAddress) + index;
    newInterface.gatesCount = 0;
    newInterface.switchboardsCount = 0;
    return newInterface;
  }

  updateScsError(hasError: boolean): void {
    this.anyScsGateError = hasError;
  }

  handleBackButton(): void {
    // Step 1 (gate config) -> Step 0 (riser config)
    if (this.step === 1) {
      this.resetGateConfiguration();
      this.step = 0;
      return;
    }

    // First riser -> previous wizard page
    if (this.currentRiserIndex === 0) {
      this.navigateToPreviousWizardPage();
    } else {
      // Go back to previous riser
      this.currentRiserIndex--;
      this.resetGateConfiguration();
      this.step = 0;
      this.initializeDataForCurrentRiser();
    }
  }

  async handleNextButton(): Promise<void> {
    if (this.step === 0) {
      await this.handleRiserConfigurationNext();
    } else if (this.step === 1) {
      await this.handleGateConfigurationNext();
    }
  }

  private async handleRiserConfigurationNext(): Promise<void> {
    await this.updatePlantStructure(this.getDataToSaveRiser());
    this.cleanupOrphanedGateConfigs();

    if (this.currentRiserGatesCount > 0) {
      this.initializeGatesToConfigure();
      this.step = 1;
    } else {
      this.moveToNextRiserOrFinish();
    }
  }

  private cleanupOrphanedGateConfigs(): void {
    if (!this.structureCreate.gateConfigStructure) return;

    // Ottieni tutti gli ID dei gate validi dal backend
    const allValidGateIds = new Set(
      (this.structureCreate.pePlantConfig?.pePlantConfig?.gates ?? []).map(gate => gate.id)
    );

    // Rimuovi i gate orfani da gateConfigStructure
    for (const gateId of Object.keys(this.structureCreate.gateConfigStructure)) {
      if (!allValidGateIds.has(gateId)) {
        delete this.structureCreate.gateConfigStructure[gateId];
      }
    }
  }

    private cleanupOrphanedRisersConfig(): void {
    if (!this.structureCreate.pePlantConfig?.riserInterfacesConfig) return;

    // Ottieni tutti gli ID dei riser validi dal backend
    const allValidRiserIds = new Set(
      (this.structureCreate.pePlantConfig?.pePlantConfig?.risers ?? []).map(riser => riser.id)
    );

    // Rimuovi i riser orfani da riserInterfacesConfig
    for (const riserId of Object.keys(this.structureCreate.pePlantConfig.riserInterfacesConfig)) {
      if (!allValidRiserIds.has(riserId)) {
        delete this.structureCreate.pePlantConfig.riserInterfacesConfig[riserId];
      }
    }
  }

  private async handleGateConfigurationNext(): Promise<void> {
    await this.updatePlantStructure(this.getDataToSaveGates());
    this.moveToNextRiserOrFinish();
  }

  private navigateToPreviousWizardPage(): void {
    const thereIsBackboneGates = this.structureCreate.pePlantConfig?.pePlantConfig?.gates?.some(gate => gate.inBackboneInterface) ?? false;
    if (thereIsBackboneGates) {
      this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BACKBONE_GATES_CONFIG;
      this.$emit('onBack', 'DialogEntrancePlantCreateSetupGate');
    } else {
      this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.RISER_AND_BACKBONE_CONFIG;
      this.$emit('onBack', 'DialogEntrancePlantCreateRiserOrNot');
    }
  }

  private moveToNextRiserOrFinish(): void {
    if (this.hasMoreRisers) {
      this.resetGateConfiguration();
      this.currentRiserIndex++;
      this.step = 0;
      this.initializeDataForCurrentRiser();
    } else {
      this.navigateToNextWizardStep();
    }
  }

  private navigateToNextWizardStep(): void {
    this.structureCreate.previousWizardPage = 'DialogEntrancePlantCreateSetupRiser';
    this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BUILDINGS_CONFIG;
    this.$emit('onNext', 'DialogEntrancePlantCreateSetupAddBuildings');
  }

  private resetGateConfiguration(): void {
    this.gatesToConfigure = {};
    this.anyScsGateError = false;
  }

  private initializeGatesToConfigure(): void {
    this.gatesToConfigure = {};
    
    for (const gate of this.currentRiserGates) {
      const gateId = gate.id!;
      const existingConfig = this.structureCreate.gateConfigStructure?.[gateId];

      this.gatesToConfigure[gateId] = new GateConfigDTO({
        gateName: existingConfig?.gateName ?? gate.name,
        scsAddress: existingConfig?.scsAddress ?? this.getValidScsAddressForNewGate(),
        isOnBackbone: existingConfig?.isOnBackbone ?? false,
        newPeSelected: existingConfig?.newPeSelected ?? false,
        keypadSelected: existingConfig?.keypadSelected ?? false,
        smartReaderSelected: existingConfig?.smartReaderSelected ?? false,
        connectedRiserIds: [this.currentRiserId],
      });
    }

  }

  private getValidScsAddressForNewGate(): number {
    const MIN_SCS_ADDRESS = 0;
    const MAX_SCS_ADDRESS = 95;

    // Indirizzi usati dai gate attualmente in configurazione (riser corrente)
    const usedAddressesInCurrentConfig = Object.values(this.gatesToConfigure)
      .map((gate: GateConfigDTO) => gate.scsAddress!)
      .filter((addr): addr is number => addr !== undefined && addr !== null)
      .map((addr) => Number(addr));

    // Indirizzi già salvati nei riser precedenti (dalla struttura)
    const usedAddressesInPreviousRisers = Object.values(this.structureCreate.gateConfigStructure ?? {})
      .map((gate: GateConfigDTO) => gate.scsAddress!)
      .filter((addr): addr is number => addr !== undefined && addr !== null)
      .map((addr) => Number(addr));

    // Trova l'indirizzo massimo usato dai riser precedenti
    const maxAddressInPreviousRisers = usedAddressesInPreviousRisers.length > 0
      ? Math.max(...usedAddressesInPreviousRisers)
      : -1;

    // Se ci sono riser precedenti, l'indirizzo minimo da cui partire è maxAddress + 2 (saltiamo +1)
    const minStartAddress = maxAddressInPreviousRisers >= 0 ? maxAddressInPreviousRisers + 2 : MIN_SCS_ADDRESS;

    // Unisci tutti gli indirizzi usati
    const allUsedAddresses = [...usedAddressesInCurrentConfig, ...usedAddressesInPreviousRisers];

    for (let addr = minStartAddress; addr <= MAX_SCS_ADDRESS; addr++) {
      const availableInPlant = this.availableScsAddressGate?.includes(addr) ?? false;
      const usedByOtherGate = allUsedAddresses.includes(addr);

      if (availableInPlant && !usedByOtherGate) {
        return addr;
      }
    }

    return MAX_SCS_ADDRESS;
  }

  private initializeDataForCurrentRiser(): void {
    const currentRiserId = this.currentRiserId;

    if (!(currentRiserId in this.riserInterfacesToConfigure)) {
      this.riserInterfacesToConfigure[currentRiserId] = [this.defaultRiserInterface(currentRiserId)];
      this.mainScsAddress = this.getValidScsAddressForNewInterface();
      this.riserInterfacesToConfigure[currentRiserId][0].scsAddress = this.mainScsAddress;
      this.InterfaceComboMode = RiserInterfaceModeCombo.Mode5IndipendentRiser;
    } else {
      this.loadExistingRiserConfiguration(currentRiserId);
    }

    this.resetValidation();
    this.validateAddresses();
  }

  private loadExistingRiserConfiguration(riserId: string): void {
    const existingInterfaces = this.riserInterfacesToConfigure[riserId];
    if (existingInterfaces.length === 0) return;

    this.mainScsAddress = existingInterfaces[0].scsAddress!;

    switch (existingInterfaces.length) {
      case 1:
        this.InterfaceComboMode = RiserInterfaceModeCombo.Mode5IndipendentRiser;
        break;
      case 2:
        this.InterfaceComboMode = RiserInterfaceModeCombo.Mode6ExtendedRiserAndOneMode7ExtendedRiserExpansion;
        break;
      default:
        this.InterfaceComboMode = RiserInterfaceModeCombo.Mode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion;
    }
  }

  private resetValidation(): void {
    this.addrNotValid = [];
    this.invalidAddrReason = [];
  }

  private getDataToSaveRiser(): EntrancePlantStructureCreateDTO {
    const updatedStructure = { ...this.structureCreate };
    if (updatedStructure.pePlantConfig) {
      updatedStructure.pePlantConfig.riserInterfacesConfig = this.riserInterfacesToConfigure;
      updatedStructure.pePlantConfig.currentStep = PlantConfigStep.RISERS_CONFIG
    }
    return new EntrancePlantStructureCreateDTO({
      pePlantConfig: updatedStructure.pePlantConfig,
      riserFlow: true,
    });
  }

  private getDataToSaveGates(): EntrancePlantStructureCreateDTO {
    const updatedStructure = { ...this.structureCreate };
    // if (!updatedStructure.gateConfigStructure) {
    //   updatedStructure.gateConfigStructure = {};
    // }
    // Object.assign(updatedStructure.gateConfigStructure, this.gatesToConfigure);

    if (updatedStructure.pePlantConfig) {
      updatedStructure.pePlantConfig.currentStep = PlantConfigStep.RISER_GATES_CONFIG;
    }

    // return new EntrancePlantStructureCreateDTO({
    //   gateConfigStructure: updatedStructure.gateConfigStructure,
    //   pePlantConfig: updatedStructure.pePlantConfig,
    //   riserFlow: true,
    // });

    return new EntrancePlantStructureCreateDTO({
      gateConfigStructure: this.gatesToConfigure,
      pePlantConfig: updatedStructure.pePlantConfig,
      riserFlow: true,
    });
  }

  private async updatePlantStructure(structure: EntrancePlantStructureCreateDTO): Promise<void> {
    if (!this.plantId) return;

    this.$emit('is-loading', true);

    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.pePlantConfig = result.pePlantConfig;
      this.riserInterfacesToConfigure = this.structureCreate.pePlantConfig?.riserInterfacesConfig || {};
      this.structureCreate.gateConfigStructure = { ...this.structureCreate.gateConfigStructure, ...result.gateConfigStructure };
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
}